import { SUPPORTED_LANGUAGES, type LanguageSetting, type SupportedLanguage, isSupportedLanguage } from "../core/language";
import { GENERATED_LANGUAGE_TEXT } from "./i18nGenerated";

export type StaticTextKey =
  | "startButton"
  | "ranking"
  | "achievements"
  | "help"
  | "settings"
  | "gameActions"
  | "board"
  | "status"
  | "score"
  | "level"
  | "chain"
  | "types"
  | "next"
  | "nextPair"
  | "topTen"
  | "touchControls"
  | "left"
  | "right"
  | "rotateLeft"
  | "rotateRight"
  | "softDrop"
  | "hardDrop"
  | "close"
  | "ok"
  | "bgm"
  | "se"
  | "touchControlsSetting"
  | "installApp"
  | "language";

export interface HelpSectionText {
  title: string;
  lines: string[];
}

export interface LocalizedText {
  languageName: string;
  locale: string;
  dir: "ltr" | "rtl";
  static: Record<StaticTextKey, string>;
  prompt: {
    start: string;
    resume: string;
    paused: string;
  };
  modal: {
    nameEntry: string;
    ranking: string;
    achievements: string;
    help: string;
    settings: string;
  };
  settings: {
    autoLanguage: string;
  };
  achievements: {
    highScore: string;
    maxLevel: string;
    maxChain: string;
    maxSimultaneousErase: string;
    resetRecords: string;
    resetConfirm: string;
  };
  share: {
    label: string;
    x: string;
    facebook: string;
    clipboard: string;
    resultsTitle: string;
    play: string;
  };
  toast: {
    copied: string;
    copyFailed: string;
    recordsReset: string;
    chain: string;
    level: string;
  };
  canvas: {
    gameOver: string;
    chain: string;
    colorCombos: string[];
    colorsFallback: string;
  };
  nameEntry: {
    score: string;
    rank: string;
  };
  helpSections: HelpSectionText[];
}

export interface LanguageOption {
  value: LanguageSetting;
  label: string;
}

export interface ResolvedLanguage {
  requested: LanguageSetting;
  language: SupportedLanguage;
  text: LocalizedText;
}

const en: LocalizedText = {
  languageName: "English",
  locale: "en-US",
  dir: "ltr",
  static: {
    startButton: "Start",
    ranking: "Ranking",
    achievements: "Achievements",
    help: "Help",
    settings: "Settings",
    gameActions: "Game actions",
    board: "Friel board",
    status: "Game status",
    score: "Score",
    level: "Level",
    chain: "Chain",
    types: "Types",
    next: "Next",
    nextPair: "Next Pair",
    topTen: "Top 10",
    touchControls: "Touch controls",
    left: "Left",
    right: "Right",
    rotateLeft: "Rotate left",
    rotateRight: "Rotate right",
    softDrop: "Soft drop",
    hardDrop: "Hard drop",
    close: "Close",
    ok: "OK",
    bgm: "BGM",
    se: "SE",
    touchControlsSetting: "Touch Controls",
    installApp: "Install app",
    language: "Language"
  },
  prompt: {
    start: "START",
    resume: "RESUME",
    paused: "PAUSED"
  },
  modal: {
    nameEntry: "Name Entry",
    ranking: "Ranking",
    achievements: "Achievements",
    help: "Help",
    settings: "Settings"
  },
  settings: {
    autoLanguage: "Auto ({language})"
  },
  achievements: {
    highScore: "High Score",
    maxLevel: "Max Level",
    maxChain: "Max Chain",
    maxSimultaneousErase: "Max Simultaneous Erase",
    resetRecords: "Reset Records",
    resetConfirm: "All achievement and ranking records will be cleared. Continue?"
  },
  share: {
    label: "Share:",
    x: "Share on X",
    facebook: "Share on Facebook",
    clipboard: "Copy results to clipboard",
    resultsTitle: "Friel Results",
    play: "Play Friel"
  },
  toast: {
    copied: "COPIED",
    copyFailed: "COPY FAILED",
    recordsReset: "RECORDS RESET",
    chain: "CHAIN {value}",
    level: "LEVEL {value}"
  },
  canvas: {
    gameOver: "GAME OVER",
    chain: "{value} Chain!",
    colorCombos: ["", "", "Double!", "Triple!", "Quadruple!", "Quintuple!", "Sextuple!"],
    colorsFallback: "{value} Colors!"
  },
  nameEntry: {
    score: "Score",
    rank: "Rank"
  },
  helpSections: [
    {
      title: "Rules",
      lines: [
        "A falling pair enters from the top. Move it left or right, rotate it around the initially lower character, then land it on the grid.",
        "If five or more of the same character exist inside any 3x3 area, those matching characters are erased.",
        "After a pair lands or characters erase, unsupported characters fall with gravity. If that fall creates another erase, it becomes a chain.",
        "The game starts with 4 character types. Level 11 adds a fifth type, and Level 21 adds a sixth type.",
        "Level advances for every 10 erased characters. Score increases with level, erased count, and chain count.",
        "Game over occurs when the next pair cannot enter the visible top of the grid."
      ]
    },
    {
      title: "Keyboard",
      lines: [
        "Start / confirm / resume: Enter",
        "Move left / right: Arrow Left / Arrow Right, or A / D",
        "Rotate counterclockwise: Q or Z",
        "Rotate clockwise: E, W, X, or Arrow Up",
        "Soft drop one row: S or Arrow Down",
        "Hard drop to the landing position: Space",
        "Pause / back: Escape",
        "Open help: F1"
      ]
    },
    {
      title: "Touch Buttons",
      lines: [
        "Left / right arrows move the falling pair one column.",
        "Curved arrows rotate counterclockwise or clockwise.",
        "Down arrow performs a soft drop by one row.",
        "Hard drop button sends the pair directly to the landing position.",
        "The pause button in the top action bar pauses the game, then changes to resume."
      ]
    },
    {
      title: "Board Gestures",
      lines: [
        "Tap the board on the title screen to start, or while paused to resume.",
        "Tap the board during play to rotate clockwise.",
        "Swipe left or right on the board to move one column.",
        "Swipe down to hard drop.",
        "Swipe up to rotate clockwise."
      ]
    },
    {
      title: "Gamepad",
      lines: [
        "D-pad or left stick left / right moves the pair.",
        "D-pad or left stick down soft drops.",
        "D-pad or left stick up hard drops.",
        "Bottom face button rotates clockwise; right face button rotates counterclockwise.",
        "Start confirms or resumes; Select / Back pauses or cancels."
      ]
    }
  ]
};

const TEXT: Record<SupportedLanguage, LocalizedText> = {
  en,
  ja: {
    languageName: "日本語",
    locale: "ja-JP",
    dir: "ltr",
    static: {
      startButton: "スタート",
      ranking: "ランキング",
      achievements: "実績",
      help: "ヘルプ",
      settings: "設定",
      gameActions: "ゲーム操作",
      board: "Friel盤面",
      status: "ゲーム状況",
      score: "スコア",
      level: "レベル",
      chain: "連鎖",
      types: "種類",
      next: "次",
      nextPair: "次のペア",
      touchControls: "タッチ操作",
      left: "左",
      right: "右",
      rotateLeft: "左回転",
      rotateRight: "右回転",
      softDrop: "ソフトドロップ",
      hardDrop: "ハードドロップ",
      close: "閉じる",
      ok: "OK",
      bgm: "BGM",
      se: "SE",
      touchControlsSetting: "タッチ操作",
      installApp: "アプリをインストール",
      language: "言語",
      topTen: "トップ10"
    },
    prompt: { start: "スタート", resume: "再開", paused: "一時停止" },
    modal: { nameEntry: "名前入力", ranking: "ランキング", achievements: "実績", help: "ヘルプ", settings: "設定" },
    settings: { autoLanguage: "自動（{language}）" },
    achievements: {
      highScore: "最高スコア",
      maxLevel: "最大レベル",
      maxChain: "最大連鎖",
      maxSimultaneousErase: "最大同時消し",
      resetRecords: "記録をリセット",
      resetConfirm: "実績とランキングの全記録を消去します。続行しますか？"
    },
    share: { label: "共有:", x: "Xで共有", facebook: "Facebookで共有", clipboard: "成績をクリップボードにコピー", resultsTitle: "Friel の成績", play: "Frielをプレイ" },
    toast: { copied: "コピーしました", copyFailed: "コピー失敗", recordsReset: "記録をリセット", chain: "{value} 連鎖", level: "レベル {value}" },
    canvas: { gameOver: "ゲームオーバー", chain: "{value} 連鎖!", colorCombos: ["", "", "ダブル!", "トリプル!", "4色同時!", "5色同時!", "6色同時!"], colorsFallback: "{value}色同時!" },
    nameEntry: { score: "スコア", rank: "順位" },
    helpSections: [
      { title: "ルール", lines: ["落下ペアは上から出現します。左右に動かし、最初に下側だったキャラクターを軸に回転させ、グリッドへ着地させます。", "3x3範囲内に同じキャラクターが5つ以上あると、そのキャラクターが消えます。", "ペアの着地後や消去後、支えのないキャラクターは重力で落下します。その落下で再び消えると連鎖になります。", "ゲーム開始時は4種類です。レベル11で5種類目、レベル21で6種類目が追加されます。", "10個消すごとにレベルが上がります。スコアはレベル、消去数、連鎖数で増えます。", "次のペアが見えている最上段に入れないとゲームオーバーです。"] },
      { title: "キーボード", lines: ["開始 / 決定 / 再開: Enter", "左右移動: ← / →、または A / D", "反時計回り回転: Q または Z", "時計回り回転: E、W、X、または ↑", "1段ソフトドロップ: S または ↓", "着地点までハードドロップ: Space", "一時停止 / 戻る: Escape", "ヘルプを開く: F1"] },
      { title: "タッチボタン", lines: ["左右矢印で落下ペアを1列移動します。", "曲がった矢印で反時計回りまたは時計回りに回転します。", "下矢印で1段ソフトドロップします。", "ハードドロップボタンで着地点まで一気に落とします。"] },
      { title: "盤面ジェスチャ", lines: ["タイトル画面では盤面タップで開始、一時停止中は再開します。", "プレイ中に盤面をタップすると時計回りに回転します。", "左右スワイプで1列移動します。", "下スワイプでハードドロップします。", "上スワイプで時計回りに回転します。"] },
      { title: "ゲームパッド", lines: ["十字キーまたは左スティック左右で移動します。", "十字キーまたは左スティック下でソフトドロップします。", "十字キーまたは左スティック上でハードドロップします。", "下側フェイスボタンで時計回り、右側フェイスボタンで反時計回りに回転します。", "Startで決定または再開、Select / Backで一時停止またはキャンセルします。"] }
    ]
  },
  "zh-Hans": {
    languageName: "简体中文",
    locale: "zh-CN",
    dir: "ltr",
    static: {
      startButton: "开始",
      ranking: "排行榜",
      achievements: "成就",
      help: "帮助",
      settings: "设置",
      gameActions: "游戏操作",
      board: "Friel 棋盘",
      status: "游戏状态",
      score: "分数",
      level: "等级",
      chain: "连锁",
      types: "种类",
      next: "下一个",
      nextPair: "下一组",
      touchControls: "触控操作",
      left: "左",
      right: "右",
      rotateLeft: "向左旋转",
      rotateRight: "向右旋转",
      softDrop: "软降",
      hardDrop: "硬降",
      close: "关闭",
      ok: "确定",
      bgm: "BGM",
      se: "音效",
      touchControlsSetting: "触控操作",
      installApp: "安装应用",
      language: "语言",
      topTen: "前10名"
    },
    prompt: { start: "开始", resume: "继续", paused: "已暂停" },
    modal: { nameEntry: "输入名称", ranking: "排行榜", achievements: "成就", help: "帮助", settings: "设置" },
    settings: { autoLanguage: "自动（{language}）" },
    achievements: { highScore: "最高分", maxLevel: "最高等级", maxChain: "最大连锁", maxSimultaneousErase: "最大同时消除", resetRecords: "重置记录", resetConfirm: "所有成就和排行榜记录都会被清除。继续吗？" },
    share: { label: "分享:", x: "分享到 X", facebook: "分享到 Facebook", clipboard: "复制成绩", resultsTitle: "Friel 成绩", play: "游玩 Friel" },
    toast: { copied: "已复制", copyFailed: "复制失败", recordsReset: "记录已重置", chain: "{value} 连锁", level: "等级 {value}" },
    canvas: { gameOver: "游戏结束", chain: "{value} 连锁!", colorCombos: ["", "", "双重!", "三重!", "四重!", "五重!", "六重!"], colorsFallback: "{value} 色!" },
    nameEntry: { score: "分数", rank: "名次" },
    helpSections: [
      { title: "规则", lines: ["下落的一组方块从顶部进入。左右移动，并围绕最初位于下方的角色旋转，然后落到网格中。", "任意 3x3 区域内有 5 个以上相同角色时，这些角色会被消除。", "一组落地或角色消除后，没有支撑的角色会受重力下落。若下落后再次消除，就形成连锁。", "游戏开始时有 4 种角色。等级 11 增加第 5 种，等级 21 增加第 6 种。", "每消除 10 个角色提升等级。分数会根据等级、消除数量和连锁数增加。", "下一组无法进入可见最上行时游戏结束。"] },
      { title: "键盘", lines: ["开始 / 确认 / 继续: Enter", "左右移动: ← / →，或 A / D", "逆时针旋转: Q 或 Z", "顺时针旋转: E、W、X 或 ↑", "软降一行: S 或 ↓", "硬降到落点: Space", "暂停 / 返回: Escape", "打开帮助: F1"] },
      { title: "触控按钮", lines: ["左右箭头将下落组移动一列。", "弯箭头用于逆时针或顺时针旋转。", "下箭头软降一行。", "硬降按钮会直接落到着地点。"] },
      { title: "棋盘手势", lines: ["标题画面点击棋盘开始，暂停时点击继续。", "游玩中点击棋盘会顺时针旋转。", "左右滑动移动一列。", "向下滑动硬降。", "向上滑动顺时针旋转。"] },
      { title: "手柄", lines: ["方向键或左摇杆左右移动。", "方向键或左摇杆向下软降。", "方向键或左摇杆向上硬降。", "下方脸键顺时针旋转，右方脸键逆时针旋转。", "Start 确认或继续，Select / Back 暂停或取消。"] }
    ]
  },
  "zh-Hant": {
    languageName: "繁體中文",
    locale: "zh-TW",
    dir: "ltr",
    static: {
      startButton: "開始",
      ranking: "排行榜",
      achievements: "成就",
      help: "說明",
      settings: "設定",
      gameActions: "遊戲操作",
      board: "Friel 盤面",
      status: "遊戲狀態",
      score: "分數",
      level: "等級",
      chain: "連鎖",
      types: "種類",
      next: "下一個",
      nextPair: "下一組",
      touchControls: "觸控操作",
      left: "左",
      right: "右",
      rotateLeft: "向左旋轉",
      rotateRight: "向右旋轉",
      softDrop: "軟降",
      hardDrop: "硬降",
      close: "關閉",
      ok: "確定",
      bgm: "BGM",
      se: "音效",
      touchControlsSetting: "觸控操作",
      installApp: "安裝應用",
      language: "語言",
      topTen: "前10名"
    },
    prompt: { start: "開始", resume: "繼續", paused: "已暫停" },
    modal: { nameEntry: "輸入名稱", ranking: "排行榜", achievements: "成就", help: "說明", settings: "設定" },
    settings: { autoLanguage: "自動（{language}）" },
    achievements: { highScore: "最高分", maxLevel: "最高等級", maxChain: "最大連鎖", maxSimultaneousErase: "最大同時消除", resetRecords: "重設記錄", resetConfirm: "所有成就與排行榜記錄都會被清除。要繼續嗎？" },
    share: { label: "分享:", x: "分享到 X", facebook: "分享到 Facebook", clipboard: "複製成績", resultsTitle: "Friel 成績", play: "遊玩 Friel" },
    toast: { copied: "已複製", copyFailed: "複製失敗", recordsReset: "記錄已重設", chain: "{value} 連鎖", level: "等級 {value}" },
    canvas: { gameOver: "遊戲結束", chain: "{value} 連鎖!", colorCombos: ["", "", "雙重!", "三重!", "四重!", "五重!", "六重!"], colorsFallback: "{value} 色!" },
    nameEntry: { score: "分數", rank: "名次" },
    helpSections: [
      { title: "規則", lines: ["下落的一組角色會從頂部進入。左右移動，並以最初位於下方的角色為軸旋轉，然後落到格子中。", "任意 3x3 區域內有 5 個以上相同角色時，這些角色會被消除。", "角色落地或消除後，沒有支撐的角色會受重力落下。若落下後再次消除，就形成連鎖。", "遊戲開始時有 4 種角色。等級 11 增加第 5 種，等級 21 增加第 6 種。", "每消除 10 個角色提升等級。分數會依等級、消除數與連鎖數增加。", "下一組無法進入可見最上排時遊戲結束。"] },
      { title: "鍵盤", lines: ["開始 / 確認 / 繼續: Enter", "左右移動: ← / →，或 A / D", "逆時針旋轉: Q 或 Z", "順時針旋轉: E、W、X 或 ↑", "軟降一列: S 或 ↓", "硬降到落點: Space", "暫停 / 返回: Escape", "開啟說明: F1"] },
      { title: "觸控按鈕", lines: ["左右箭頭將下落組移動一欄。", "彎箭頭可逆時針或順時針旋轉。", "下箭頭軟降一列。", "硬降按鈕會直接落到著地點。"] },
      { title: "盤面手勢", lines: ["標題畫面點擊盤面開始，暫停時點擊繼續。", "遊玩中點擊盤面會順時針旋轉。", "左右滑動移動一欄。", "向下滑動硬降。", "向上滑動順時針旋轉。"] },
      { title: "控制器", lines: ["方向鍵或左搖桿左右移動。", "方向鍵或左搖桿向下軟降。", "方向鍵或左搖桿向上硬降。", "下方臉鍵順時針旋轉，右方臉鍵逆時針旋轉。", "Start 確認或繼續，Select / Back 暫停或取消。"] }
    ]
  },
  ko: {
    languageName: "한국어",
    locale: "ko-KR",
    dir: "ltr",
    static: {
      startButton: "시작",
      ranking: "랭킹",
      achievements: "업적",
      help: "도움말",
      settings: "설정",
      gameActions: "게임 조작",
      board: "Friel 보드",
      status: "게임 상태",
      score: "점수",
      level: "레벨",
      chain: "연쇄",
      types: "종류",
      next: "다음",
      nextPair: "다음 페어",
      touchControls: "터치 조작",
      left: "왼쪽",
      right: "오른쪽",
      rotateLeft: "왼쪽 회전",
      rotateRight: "오른쪽 회전",
      softDrop: "소프트 드롭",
      hardDrop: "하드 드롭",
      close: "닫기",
      ok: "OK",
      bgm: "BGM",
      se: "효과음",
      touchControlsSetting: "터치 조작",
      installApp: "앱 설치",
      language: "언어",
      topTen: "상위 10"
    },
    prompt: { start: "시작", resume: "재개", paused: "일시정지" },
    modal: { nameEntry: "이름 입력", ranking: "랭킹", achievements: "업적", help: "도움말", settings: "설정" },
    settings: { autoLanguage: "자동 ({language})" },
    achievements: { highScore: "최고 점수", maxLevel: "최대 레벨", maxChain: "최대 연쇄", maxSimultaneousErase: "최대 동시 삭제", resetRecords: "기록 초기화", resetConfirm: "모든 업적과 랭킹 기록이 삭제됩니다. 계속할까요?" },
    share: { label: "공유:", x: "X에 공유", facebook: "Facebook에 공유", clipboard: "성적 복사", resultsTitle: "Friel 성적", play: "Friel 플레이" },
    toast: { copied: "복사됨", copyFailed: "복사 실패", recordsReset: "기록 초기화됨", chain: "{value} 연쇄", level: "레벨 {value}" },
    canvas: { gameOver: "게임 오버", chain: "{value} 연쇄!", colorCombos: ["", "", "더블!", "트리플!", "4색 동시!", "5색 동시!", "6색 동시!"], colorsFallback: "{value}색 동시!" },
    nameEntry: { score: "점수", rank: "순위" },
    helpSections: [
      { title: "규칙", lines: ["떨어지는 페어는 위에서 등장합니다. 좌우로 이동하고 처음 아래쪽에 있던 캐릭터를 축으로 회전한 뒤 보드에 착지시킵니다.", "3x3 영역 안에 같은 캐릭터가 5개 이상 있으면 그 캐릭터들이 사라집니다.", "페어 착지나 삭제 후 지지되지 않은 캐릭터는 중력으로 떨어집니다. 그 낙하로 다시 삭제되면 연쇄가 됩니다.", "게임은 4종류로 시작합니다. 레벨 11에서 5번째, 레벨 21에서 6번째 종류가 추가됩니다.", "10개를 지울 때마다 레벨이 오릅니다. 점수는 레벨, 삭제 수, 연쇄 수에 따라 증가합니다.", "다음 페어가 보이는 맨 위 줄에 들어갈 수 없으면 게임 오버입니다."] },
      { title: "키보드", lines: ["시작 / 확인 / 재개: Enter", "좌우 이동: ← / → 또는 A / D", "반시계 회전: Q 또는 Z", "시계 회전: E, W, X 또는 ↑", "한 줄 소프트 드롭: S 또는 ↓", "착지 위치까지 하드 드롭: Space", "일시정지 / 뒤로: Escape", "도움말 열기: F1"] },
      { title: "터치 버튼", lines: ["좌우 화살표는 페어를 한 칸 이동합니다.", "굽은 화살표는 반시계 또는 시계 방향으로 회전합니다.", "아래 화살표는 한 줄 소프트 드롭합니다.", "하드 드롭 버튼은 착지 위치까지 바로 떨어뜨립니다."] },
      { title: "보드 제스처", lines: ["타이틀 화면에서 보드를 탭하면 시작하고, 일시정지 중에는 재개합니다.", "플레이 중 보드를 탭하면 시계 방향으로 회전합니다.", "좌우 스와이프로 한 칸 이동합니다.", "아래로 스와이프하면 하드 드롭합니다.", "위로 스와이프하면 시계 방향으로 회전합니다."] },
      { title: "게임패드", lines: ["D-pad 또는 왼쪽 스틱 좌우로 이동합니다.", "D-pad 또는 왼쪽 스틱 아래로 소프트 드롭합니다.", "D-pad 또는 왼쪽 스틱 위로 하드 드롭합니다.", "아래쪽 버튼은 시계 방향, 오른쪽 버튼은 반시계 방향으로 회전합니다.", "Start는 확인 또는 재개, Select / Back은 일시정지 또는 취소입니다."] }
    ]
  },
  es: makeRomanceText("Español", "es-ES", {
    start: "Iniciar",
    ranking: "Clasificación",
    achievements: "Logros",
    help: "Ayuda",
    settings: "Ajustes",
    score: "Puntos",
    level: "Nivel",
    chain: "Cadena",
    types: "Tipos",
    next: "Siguiente",
    nextPair: "Siguiente pareja",
    close: "Cerrar",
    language: "Idioma",
    promptStart: "INICIAR",
    resume: "CONTINUAR",
    paused: "PAUSA",
    nameEntry: "Nombre",
    highScore: "Mejor puntuación",
    maxLevel: "Nivel máximo",
    maxChain: "Cadena máxima",
    maxSimultaneousErase: "Máximo borrado simultáneo",
    reset: "Reiniciar récords",
    resetConfirm: "Se borrarán todos los logros y la clasificación. ¿Continuar?",
    shareResults: "Resultados de Friel",
    play: "Juega a Friel",
    copied: "COPIADO",
    copyFailed: "ERROR AL COPIAR",
    recordsReset: "RÉCORDS REINICIADOS",
    rules: "Reglas",
    keyboard: "Teclado",
    touchButtons: "Botones táctiles",
    gestures: "Gestos del tablero",
    gamepad: "Mando",
    localeChain: "{value} Chain!",
    gameOver: "GAME OVER"
  }),
  fr: makeRomanceText("Français", "fr-FR", {
    start: "Démarrer",
    ranking: "Classement",
    achievements: "Succès",
    help: "Aide",
    settings: "Réglages",
    score: "Score",
    level: "Niveau",
    chain: "Chaîne",
    types: "Types",
    next: "Suivant",
    nextPair: "Paire suivante",
    close: "Fermer",
    language: "Langue",
    promptStart: "DÉMARRER",
    resume: "REPRENDRE",
    paused: "PAUSE",
    nameEntry: "Nom",
    highScore: "Meilleur score",
    maxLevel: "Niveau max",
    maxChain: "Chaîne max",
    maxSimultaneousErase: "Max effacé en même temps",
    reset: "Réinitialiser",
    resetConfirm: "Tous les succès et classements seront effacés. Continuer ?",
    shareResults: "Résultats Friel",
    play: "Jouer à Friel",
    copied: "COPIÉ",
    copyFailed: "ÉCHEC COPIE",
    recordsReset: "RÉCORDS RÉINITIALISÉS",
    rules: "Règles",
    keyboard: "Clavier",
    touchButtons: "Boutons tactiles",
    gestures: "Gestes du plateau",
    gamepad: "Manette",
    localeChain: "{value} Chain!",
    gameOver: "GAME OVER"
  }),
  de: makeRomanceText("Deutsch", "de-DE", {
    start: "Start",
    ranking: "Rangliste",
    achievements: "Erfolge",
    help: "Hilfe",
    settings: "Einstellungen",
    score: "Punkte",
    level: "Level",
    chain: "Kette",
    types: "Arten",
    next: "Nächstes",
    nextPair: "Nächstes Paar",
    close: "Schließen",
    language: "Sprache",
    promptStart: "START",
    resume: "WEITER",
    paused: "PAUSE",
    nameEntry: "Name",
    highScore: "Bestpunktzahl",
    maxLevel: "Max. Level",
    maxChain: "Max. Kette",
    maxSimultaneousErase: "Max. gleichzeitig gelöscht",
    reset: "Rekorde löschen",
    resetConfirm: "Alle Erfolge und Ranglisten werden gelöscht. Fortfahren?",
    shareResults: "Friel Ergebnisse",
    play: "Friel spielen",
    copied: "KOPIERT",
    copyFailed: "KOPIEREN FEHLGESCHLAGEN",
    recordsReset: "REKORDE GELÖSCHT",
    rules: "Regeln",
    keyboard: "Tastatur",
    touchButtons: "Touch-Tasten",
    gestures: "Brettgesten",
    gamepad: "Gamepad",
    localeChain: "{value} Chain!",
    gameOver: "GAME OVER"
  }),
  pt: makeRomanceText("Português", "pt-BR", {
    start: "Iniciar",
    ranking: "Ranking",
    achievements: "Conquistas",
    help: "Ajuda",
    settings: "Configurações",
    score: "Pontuação",
    level: "Nível",
    chain: "Cadeia",
    types: "Tipos",
    next: "Próximo",
    nextPair: "Próximo par",
    close: "Fechar",
    language: "Idioma",
    promptStart: "INICIAR",
    resume: "CONTINUAR",
    paused: "PAUSADO",
    nameEntry: "Nome",
    highScore: "Maior pontuação",
    maxLevel: "Nível máximo",
    maxChain: "Maior cadeia",
    maxSimultaneousErase: "Maior eliminação simultânea",
    reset: "Redefinir recordes",
    resetConfirm: "Todas as conquistas e rankings serão apagados. Continuar?",
    shareResults: "Resultados do Friel",
    play: "Jogar Friel",
    copied: "COPIADO",
    copyFailed: "FALHA AO COPIAR",
    recordsReset: "RECORDES REDEFINIDOS",
    rules: "Regras",
    keyboard: "Teclado",
    touchButtons: "Botões de toque",
    gestures: "Gestos no tabuleiro",
    gamepad: "Controle",
    localeChain: "{value} Chain!",
    gameOver: "GAME OVER"
  }),
  it: makeRomanceText("Italiano", "it-IT", {
    start: "Avvia",
    ranking: "Classifica",
    achievements: "Obiettivi",
    help: "Aiuto",
    settings: "Impostazioni",
    score: "Punteggio",
    level: "Livello",
    chain: "Catena",
    types: "Tipi",
    next: "Prossimo",
    nextPair: "Prossima coppia",
    close: "Chiudi",
    language: "Lingua",
    promptStart: "AVVIA",
    resume: "RIPRENDI",
    paused: "PAUSA",
    nameEntry: "Nome",
    highScore: "Punteggio record",
    maxLevel: "Livello massimo",
    maxChain: "Catena massima",
    maxSimultaneousErase: "Massima eliminazione simultanea",
    reset: "Azzera record",
    resetConfirm: "Tutti gli obiettivi e la classifica saranno cancellati. Continuare?",
    shareResults: "Risultati Friel",
    play: "Gioca a Friel",
    copied: "COPIATO",
    copyFailed: "COPIA FALLITA",
    recordsReset: "RECORD AZZERATI",
    rules: "Regole",
    keyboard: "Tastiera",
    touchButtons: "Pulsanti touch",
    gestures: "Gesti sulla griglia",
    gamepad: "Controller",
    localeChain: "{value} Chain!",
    gameOver: "GAME OVER"
  }),
  ru: makeRomanceText("Русский", "ru-RU", {
    start: "Старт",
    ranking: "Рейтинг",
    achievements: "Достижения",
    help: "Помощь",
    settings: "Настройки",
    score: "Счет",
    level: "Уровень",
    chain: "Цепь",
    types: "Типы",
    next: "След.",
    nextPair: "Следующая пара",
    close: "Закрыть",
    language: "Язык",
    promptStart: "СТАРТ",
    resume: "ПРОДОЛЖИТЬ",
    paused: "ПАУЗА",
    nameEntry: "Имя",
    highScore: "Лучший счет",
    maxLevel: "Макс. уровень",
    maxChain: "Макс. цепь",
    maxSimultaneousErase: "Макс. одновременное удаление",
    reset: "Сбросить рекорды",
    resetConfirm: "Все достижения и рейтинг будут удалены. Продолжить?",
    shareResults: "Результаты Friel",
    play: "Играть в Friel",
    copied: "СКОПИРОВАНО",
    copyFailed: "КОПИРОВАНИЕ НЕ УДАЛОСЬ",
    recordsReset: "РЕКОРДЫ СБРОШЕНЫ",
    rules: "Правила",
    keyboard: "Клавиатура",
    touchButtons: "Сенсорные кнопки",
    gestures: "Жесты на поле",
    gamepad: "Геймпад",
    localeChain: "{value} Chain!",
    gameOver: "GAME OVER"
  }),
  ar: makeRomanceText("العربية", "ar", {
    start: "ابدأ",
    ranking: "الترتيب",
    achievements: "الإنجازات",
    help: "المساعدة",
    settings: "الإعدادات",
    score: "النقاط",
    level: "المستوى",
    chain: "سلسلة",
    types: "الأنواع",
    next: "التالي",
    nextPair: "الزوج التالي",
    close: "إغلاق",
    language: "اللغة",
    promptStart: "ابدأ",
    resume: "متابعة",
    paused: "إيقاف مؤقت",
    nameEntry: "إدخال الاسم",
    highScore: "أعلى نتيجة",
    maxLevel: "أعلى مستوى",
    maxChain: "أكبر سلسلة",
    maxSimultaneousErase: "أكبر حذف متزامن",
    reset: "إعادة ضبط السجلات",
    resetConfirm: "سيتم مسح كل الإنجازات والترتيب. هل تريد المتابعة؟",
    shareResults: "نتائج Friel",
    play: "العب Friel",
    copied: "تم النسخ",
    copyFailed: "فشل النسخ",
    recordsReset: "تمت إعادة الضبط",
    rules: "القواعد",
    keyboard: "لوحة المفاتيح",
    touchButtons: "أزرار اللمس",
    gestures: "إيماءات اللوحة",
    gamepad: "يد التحكم",
    localeChain: "سلسلة {value}!",
    gameOver: "GAME OVER",
    dir: "rtl"
  }),
  hi: makeRomanceText("हिन्दी", "hi-IN", {
    start: "शुरू",
    ranking: "रैंकिंग",
    achievements: "उपलब्धियां",
    help: "मदद",
    settings: "सेटिंग",
    score: "स्कोर",
    level: "लेवल",
    chain: "चेन",
    types: "प्रकार",
    next: "अगला",
    nextPair: "अगली जोड़ी",
    close: "बंद करें",
    language: "भाषा",
    promptStart: "शुरू",
    resume: "जारी रखें",
    paused: "रुका हुआ",
    nameEntry: "नाम दर्ज करें",
    highScore: "सर्वोच्च स्कोर",
    maxLevel: "अधिकतम लेवल",
    maxChain: "अधिकतम चेन",
    maxSimultaneousErase: "अधिकतम एक साथ मिटाना",
    reset: "रिकॉर्ड रीसेट",
    resetConfirm: "सभी उपलब्धियां और रैंकिंग रिकॉर्ड मिट जाएंगे। जारी रखें?",
    shareResults: "Friel परिणाम",
    play: "Friel खेलें",
    copied: "कॉपी हुआ",
    copyFailed: "कॉपी विफल",
    recordsReset: "रिकॉर्ड रीसेट हुए",
    rules: "नियम",
    keyboard: "कीबोर्ड",
    touchButtons: "टच बटन",
    gestures: "बोर्ड जेस्चर",
    gamepad: "गेमपैड",
    localeChain: "{value} Chain!",
    gameOver: "GAME OVER"
  }),
  id: makeRomanceText("Bahasa Indonesia", "id-ID", {
    start: "Mulai",
    ranking: "Peringkat",
    achievements: "Prestasi",
    help: "Bantuan",
    settings: "Pengaturan",
    score: "Skor",
    level: "Level",
    chain: "Rantai",
    types: "Jenis",
    next: "Berikutnya",
    nextPair: "Pasangan berikutnya",
    close: "Tutup",
    language: "Bahasa",
    promptStart: "MULAI",
    resume: "LANJUT",
    paused: "JEDA",
    nameEntry: "Nama",
    highScore: "Skor tertinggi",
    maxLevel: "Level maksimum",
    maxChain: "Rantai maksimum",
    maxSimultaneousErase: "Hapus bersamaan maksimum",
    reset: "Reset Rekor",
    resetConfirm: "Semua prestasi dan peringkat akan dihapus. Lanjutkan?",
    shareResults: "Hasil Friel",
    play: "Mainkan Friel",
    copied: "DISALIN",
    copyFailed: "GAGAL MENYALIN",
    recordsReset: "REKOR DI-RESET",
    rules: "Aturan",
    keyboard: "Keyboard",
    touchButtons: "Tombol sentuh",
    gestures: "Gestur papan",
    gamepad: "Gamepad",
    localeChain: "{value} Chain!",
    gameOver: "GAME OVER"
  })
};

const PWA_INSTALL_TEXT: Record<SupportedLanguage, string> = {
  en: "Install app",
  ja: "アプリをインストール",
  "zh-Hans": "安装应用",
  "zh-Hant": "安裝應用",
  ko: "앱 설치",
  es: "Instalar app",
  fr: "Installer l'application",
  de: "App installieren",
  pt: "Instalar app",
  it: "Installa app",
  ru: "Установить приложение",
  ar: "تثبيت التطبيق",
  hi: "ऐप इंस्टॉल करें",
  id: "Instal aplikasi"
};

for (const language of SUPPORTED_LANGUAGES) {
  TEXT[language].static.installApp = PWA_INSTALL_TEXT[language];
}

interface RomanceSeed {
  start: string;
  ranking: string;
  achievements: string;
  help: string;
  settings: string;
  score: string;
  level: string;
  chain: string;
  types: string;
  next: string;
  nextPair: string;
  close: string;
  language: string;
  promptStart: string;
  resume: string;
  paused: string;
  nameEntry: string;
  highScore: string;
  maxLevel: string;
  maxChain: string;
  maxSimultaneousErase: string;
  reset: string;
  resetConfirm: string;
  shareResults: string;
  play: string;
  copied: string;
  copyFailed: string;
  recordsReset: string;
  rules: string;
  keyboard: string;
  touchButtons: string;
  gestures: string;
  gamepad: string;
  localeChain: string;
  gameOver: string;
  dir?: "ltr" | "rtl";
}

function makeRomanceText(languageName: string, locale: string, seed: RomanceSeed): LocalizedText {
  const generated = GENERATED_LANGUAGE_TEXT[locale] ?? GENERATED_LANGUAGE_TEXT[locale.split("-")[0]];
  return {
    languageName,
    locale,
    dir: seed.dir ?? "ltr",
    static: {
      ...en.static,
      startButton: seed.start,
      ranking: seed.ranking,
      achievements: seed.achievements,
      help: seed.help,
      settings: seed.settings,
      gameActions: seed.settings,
      score: seed.score,
      level: seed.level,
      chain: seed.chain,
      types: seed.types,
      next: seed.next,
      nextPair: seed.nextPair,
      close: seed.close,
      language: seed.language,
      ...generated.static
    },
    prompt: { start: seed.promptStart, resume: seed.resume, paused: seed.paused },
    modal: { nameEntry: seed.nameEntry, ranking: seed.ranking, achievements: seed.achievements, help: seed.help, settings: seed.settings },
    settings: { autoLanguage: generated.autoLanguage },
    achievements: {
      highScore: seed.highScore,
      maxLevel: seed.maxLevel,
      maxChain: seed.maxChain,
      maxSimultaneousErase: seed.maxSimultaneousErase,
      resetRecords: seed.reset,
      resetConfirm: seed.resetConfirm
    },
    share: { label: generated.shareLabel, x: generated.shareX, facebook: generated.shareFacebook, clipboard: generated.shareClipboard, resultsTitle: seed.shareResults, play: seed.play },
    toast: { copied: seed.copied, copyFailed: seed.copyFailed, recordsReset: seed.recordsReset, chain: generated.canvas.chain, level: `${seed.level} {value}` },
    canvas: generated.canvas,
    nameEntry: { score: seed.score, rank: generated.rank },
    helpSections: generated.helpSections
  };
}

export function resolveLanguage(setting: LanguageSetting, browserLanguages = readBrowserLanguages()): ResolvedLanguage {
  const language = setting === "auto" ? matchBrowserLanguage(browserLanguages) : setting;
  return {
    requested: setting,
    language,
    text: TEXT[language]
  };
}

export function languageOptions(current: ResolvedLanguage): LanguageOption[] {
  return [
    {
      value: "auto",
      label: formatTemplate(current.text.settings.autoLanguage, { language: TEXT[current.language].languageName })
    },
    ...SUPPORTED_LANGUAGES.map((language) => ({
      value: language,
      label: TEXT[language].languageName
    }))
  ];
}

export function formatTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ""));
}

export function formatNumber(value: number, text: LocalizedText): string {
  return Math.max(0, Math.floor(value)).toLocaleString(text.locale);
}

export function colorComboText(text: LocalizedText, colorCount: number): string {
  return text.canvas.colorCombos[colorCount] ?? formatTemplate(text.canvas.colorsFallback, { value: colorCount });
}

function readBrowserLanguages(): string[] {
  if (typeof navigator === "undefined") {
    return ["en"];
  }
  return navigator.languages?.length ? [...navigator.languages] : [navigator.language || "en"];
}

function matchBrowserLanguage(languages: string[]): SupportedLanguage {
  for (const raw of languages) {
    const normalized = raw.toLowerCase();
    if (normalized.startsWith("zh")) {
      if (normalized.includes("hant") || normalized.includes("tw") || normalized.includes("hk") || normalized.includes("mo")) {
        return "zh-Hant";
      }
      return "zh-Hans";
    }
    const base = normalized.split("-")[0];
    if (isSupportedLanguage(base)) {
      return base;
    }
  }
  return "en";
}
